import { FC, ReactNode } from 'react';
import { Stack, Typography } from '@mui/material';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { MplTrans, useTranslation } from '@mopla/i18';
import { BlurLazyImage } from '@mopla/ui';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

import carousel_1_5x7_1x from '../assets/carousel_1_5x7_1x.webp';
import carousel_1_5x7_2x from '../assets/carousel_1_5x7_2x.webp';
import carousel_2_5x7_1x from '../assets/carousel_2_5x7_1x.webp';
import carousel_2_5x7_2x from '../assets/carousel_2_5x7_2x.webp';
import carousel_3_5x7_1x from '../assets/carousel_3_5x7_1x.webp';
import carousel_3_5x7_2x from '../assets/carousel_3_5x7_2x.webp';
import logo1 from '../assets/logo_1.svg?url';
import logo2 from '../assets/logo_2.svg?url';
import logo3 from '../assets/logo_3.svg?url';

import {
	IMAGE_1_PLACEHOLDER,
	IMAGE_2_PLACEHOLDER,
	IMAGE_3_PLACEHOLDER,
} from './constants';
import {
	LogoWrapper,
	SlideContentWrapper,
	SlideRoot,
	SwiperWrapper,
} from './styled';

interface ISlideContentLayoutProps {
	bgImage: ReactNode;
	logo: ReactNode;
	title: ReactNode;
	subtitle: ReactNode;
}

const SlideContentLayout: FC<ISlideContentLayoutProps> = ({
	bgImage,
	title,
	subtitle,
	logo,
}) => {
	return (
		<SlideRoot>
			{bgImage}
			<SlideContentWrapper>
				<LogoWrapper>{logo}</LogoWrapper>
				<Stack gap={2}>
					<Typography
						variant="h3-sb"
						color="neutral.0"
						textAlign="center"
						sx={{ userSelect: 'none' }}
					>
						{title}
					</Typography>
					<Typography
						variant="b2"
						color="neutral.0"
						textAlign="center"
						whiteSpace="pre-wrap"
						sx={{ userSelect: 'none' }}
					>
						{subtitle}
					</Typography>
				</Stack>
			</SlideContentWrapper>
		</SlideRoot>
	);
};

export const WelcomeEntryCarousel: FC = () => {
	const { t } = useTranslation('welcome');

	return (
		<SwiperWrapper>
			<Swiper
				loop
				autoplay={{ delay: 3000 }}
				pagination
				modules={[Pagination, Autoplay]}
				style={{ height: '100%' }}
				slidesPerView={1}
			>
				<SwiperSlide>
					<SlideContentLayout
						bgImage={
							<BlurLazyImage
								className="image"
								src={carousel_1_5x7_1x}
								src2x={carousel_1_5x7_2x}
								placeholderSrc={IMAGE_1_PLACEHOLDER}
								alt="Hand holding a phone with the Mopla app, in front of a Mopla van."
								width="100%"
								height="100%"
							/>
						}
						logo={<img src={logo1} width={95} alt="Mopla bewegt logo" />}
						title={t('carousel.slide1.title')}
						subtitle={<MplTrans ns="welcome" i18nKey="carousel.slide1.body" />}
					/>
				</SwiperSlide>
				<SwiperSlide>
					<SlideContentLayout
						bgImage={
							<BlurLazyImage
								className="image"
								src={carousel_2_5x7_1x}
								src2x={carousel_2_5x7_2x}
								placeholderSrc={IMAGE_2_PLACEHOLDER}
								alt="Traveler holding a phone with the Mopla app showing a Deutschland ticket QR code, next to a suitcase."
								width="100%"
								height="100%"
							/>
						}
						logo={<img src={logo2} width={95} alt="Mopla ticketing logo" />}
						title={t('carousel.slide2.title')}
						subtitle={<MplTrans ns="welcome" i18nKey="carousel.slide2.body" />}
					/>
				</SwiperSlide>
				<SwiperSlide>
					<SlideContentLayout
						bgImage={
							<BlurLazyImage
								className="image"
								src={carousel_3_5x7_1x}
								src2x={carousel_3_5x7_2x}
								placeholderSrc={IMAGE_3_PLACEHOLDER}
								alt="Man stepping out of a Mopla van with a travel bag."
								width="100%"
								height="100%"
							/>
						}
						logo={<img src={logo3} width={95} alt="Mopla myway logo" />}
						title={t('carousel.slide3.title')}
						subtitle={<MplTrans ns="welcome" i18nKey="carousel.slide3.body" />}
					/>
				</SwiperSlide>
			</Swiper>
		</SwiperWrapper>
	);
};
